import { QueryClient } from '@tanstack/react-query';
import { isNetworkError } from './graphql';

/**
 * Reintentar un error de negocio («este DNI ya existe») no lo va a arreglar y
 * sólo retrasa el mensaje. Sólo se reintenta lo que sí puede ser transitorio:
 * red caída o 5xx.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => isNetworkError(error) && failureCount < 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Claves de caché centralizadas. Tenerlas en un sitio evita el clásico
 * «invalidé `['tickets']` pero la consulta se llamaba `['ticket-list']`».
 */
export const qk = {
  me: ['me'] as const,
  users: (args?: unknown) => ['users', args ?? null] as const,

  customers: (args?: unknown) => ['customers', args ?? null] as const,
  customer: (id: string) => ['customer', id] as const,
  customerSales: (id: string, page: number) => ['customer', id, 'sales', page] as const,
  customerTickets: (id: string, args?: unknown) => ['customer', id, 'tickets', args ?? null] as const,
  customerSearch: (query: string) => ['customer-search', query] as const,

  tickets: (args?: unknown) => ['tickets', args ?? null] as const,
  ticket: (id: string) => ['ticket', id] as const,
  ticketEvents: (id: string, publicOnly: boolean | null) => ['ticket', id, 'events', publicOnly] as const,

  conversations: (args?: unknown) => ['conversations', args ?? null] as const,
  conversation: (id: string) => ['conversation', id] as const,
  messageTemplates: (channel?: string | null) => ['message-templates', channel ?? null] as const,
  botRules: (activeOnly?: boolean | null) => ['bot-rules', activeOnly ?? null] as const,

  followups: (args?: unknown) => ['followups', args ?? null] as const,
  agenda: (day: string | null, userId: string | null) => ['agenda', day, userId] as const,

  warranties: (args?: unknown) => ['warranties', args ?? null] as const,
  partRequests: (args?: unknown) => ['part-requests', args ?? null] as const,

  products: (args?: unknown) => ['products', args ?? null] as const,
  product: (id: string, range?: unknown) => ['product', id, range ?? null] as const,
  productCategories: ['product-categories'] as const,

  campaigns: (args?: unknown) => ['campaigns', args ?? null] as const,
  campaign: (id: string, page: number) => ['campaign', id, page] as const,
  audiencePreview: (filter: unknown) => ['audience-preview', filter] as const,

  sales: (args?: unknown) => ['sales', args ?? null] as const,

  dashboard: (range: unknown) => ['dashboard', range] as const,
  kpis: (keys: unknown, range: unknown) => ['kpis', keys, range] as const,
  kpiSeries: (key: string, range: unknown, granularity: string) =>
    ['kpi-series', key, range, granularity] as const,
  kpiTargets: ['kpi-targets'] as const,

  notifications: (unreadOnly: boolean) => ['notifications', unreadOnly] as const,
  unreadNotificationCount: ['unread-notification-count'] as const,

  settings: (keys: unknown) => ['settings', keys ?? null] as const,
  importJobs: (kind: unknown) => ['import-jobs', kind ?? null] as const,
} as const;
